import React from 'react';
import kebabCase from 'lodash/kebabCase';
import toPairs from 'lodash/toPairs';

// Constellation Components
import {
    Checkbox,
    Input,
    FormField,
    FormHelp,
    Label,
    LabeledControl,
    Text,
    Textarea,
} from '@zillow/constellation';

import {
    Col,
    Row,
} from '@zillow/zrs-comet';

import BuildingAmenitiesConstants from 'app/shared/constants/BuildingAmenitiesConstants';
import numberUtils from 'app/shared/utils/numberUtils';
import userUtils from 'app/shared/utils/userUtils';

// Styled Components
import {
    StyledRow,
    StyledUppercaseText,
} from './StyledComponents';

import {
    StyledAnchor,
    StyledHR,
} from '../UtilsStyledComponents';

const preserveFieldName = true;

class BuildingAttributes extends React.Component {
    constructor(props) {
        super(props);

        // bind methods
        this.renderAmenitiesList = this.renderAmenitiesList.bind(this);
        this.renderAmenitiesCheckBoxes = this.renderAmenitiesCheckBoxes.bind(this);
        this.updateFormData = this.updateFormData.bind(this);
    }

    updateFormData(event) {
        // Clear any associated errors with field when user enters input.
        const value = event.target.value;
        const propName = event.target.name;
        if (this.props.errorObj[propName] && value.length > 0) {
            this.props.updateErrorObj({ [propName]: null });
        }

        this.props.updateFormData({ [propName]: value });
    }

    renderAmenitiesCheckBoxes(amenities, amenityCategory = null, keyword) {
        const { formData, immediateUpdateAndSave, isInputFormDisabled } = this.props;

        return amenities.map((amenity) => {
            const amenityLabel = amenity[0];
            const amenityName = amenity[1];
            const amenityAPI = amenityName + keyword;
            let hasAmenity = false;

            if (formData.buildingAttributes.hasOwnProperty(amenityCategory)) {
                if (formData.buildingAttributes[amenityCategory].hasOwnProperty(amenityName)) {
                    hasAmenity = formData.buildingAttributes[amenityCategory][amenityName];
                }
            } else if (formData.buildingAttributes.hasOwnProperty(amenityName)) {
                hasAmenity = formData.buildingAttributes[amenityName];
            }

            return (
                <LabeledControl
                    key={amenityLabel}
                    controlId={amenityLabel}
                    label={<Label>{amenityLabel}</Label>}
                    control={(
                        <Checkbox
                            key={amenityLabel}
                            checked={hasAmenity === true}
                            disabled={isInputFormDisabled}
                            onChange={() => immediateUpdateAndSave(
                                amenityAPI, !hasAmenity, {
                                    category: amenityCategory,
                                    preserveFieldName,
                                    formDataName: amenityName,
                                }
                            )}
                        />
                    )}
                />
            );
        });
    }

    renderAmenitiesList(amenityType) {
        const keyword = amenityType.hasOwnProperty('keyword') ? amenityType.keyword : '';
        const amenityCategory = amenityType.hasOwnProperty('category') ? amenityType.category : null;
        const numColumns = 3;
        const amenitiesArray = toPairs(amenityType.fields);
        const rowsPerColumn = Math.ceil(amenitiesArray.length / numColumns);

        const sections = [];
        while (amenitiesArray.length > 0) {
            sections.push(amenitiesArray.splice(0, rowsPerColumn));
        }

        const content = [];
        sections.forEach((section, index) => {
            content.push(
                <Col
                    key={`section-${index}`}
                    xs={12}
                    lg={6}
                >
                    {this.renderAmenitiesCheckBoxes(section, amenityCategory, keyword)}
                </Col>
            );
        });

        return (
            <React.Fragment>
                <Col
                    marginBottom="xs"
                    mediaQuery={{ lg: { marginBottom: 'lg' } }}
                    xs={12}
                    md={6}
                >
                    <Label>
                        <StyledUppercaseText
                            fontType="bodySmall"
                        >
                            {amenityType.sectionLabel}
                        </StyledUppercaseText>
                    </Label>

                    <StyledHR marginY="xs" />

                    <StyledRow paddingY="xs">
                        {content}
                    </StyledRow>
                </Col>
            </React.Fragment>
        );
    }

    render() {
        const {
            formData,
            errorObj,
            savePropertyData,
            updateOnFocus,
            userRoles,
        } = this.props;

        return (
            <div
                className="BuildingAttributes"
            >
                <Text
                    marginBottom="xs"
                    mediaQuery={{
                        lg: {
                            marginBottom: 'sm',
                        },
                    }}
                    fontType="h4"
                    as="div"
                >
                    Building amenities
                </Text>

                <StyledAnchor />

                <Row paddingY="xs">
                    {Object.entries(BuildingAmenitiesConstants.booleanFields).map((category, index) => (
                        <React.Fragment key={index}>
                            {this.renderAmenitiesList(category[1], index)}
                        </React.Fragment>
                    ))}
                </Row>

                {userUtils.isPostingPathAdmin(userRoles) && (
                    <div>
                        <Row paddingY="xs">
                            <Col>
                                <Text
                                    paddingY="xs"
                                    as="div"
                                >
                                        The following fields are visible to employees only
                                        and always write directly to BAD;
                                        there may be a slight delay before they populate to the listing on page refresh.
                                </Text>
                            </Col>
                        </Row>
                        {Object.entries(BuildingAmenitiesConstants.stringFields).map((entry) => {
                            const [fieldLabel, apiName] = [...entry];
                            const CustomTag = apiName === 'zillowDescription' ? Textarea : Input;
                            const dataTestVal = kebabCase(fieldLabel);
                            const maxLength = BuildingAmenitiesConstants.maxLengthFields[apiName];

                            return (
                                <Row paddingY="xs" key={apiName}>
                                    <Col>
                                        <FormField>
                                            <Label>{fieldLabel}</Label>
                                            <CustomTag
                                                maxLength={maxLength}
                                                error={errorObj[apiName]}
                                                resize="both"
                                                name={apiName}
                                                type={apiName === 'zillowDescription' ? 'textarea' : 'text'}
                                                value={formData.buildingAttributes[apiName]}
                                                onChange={e => this.props.updateFormData({
                                                    buildingAttributes: {
                                                        ...formData.buildingAttributes,
                                                        [apiName]: e.target.value,
                                                    },
                                                })}
                                                onBlur={e => savePropertyData(apiName, e.target.value, {
                                                    preserveFieldName,
                                                })}
                                                onFocus={() => updateOnFocus(apiName,
                                                    formData.buildingAttributes[apiName])}
                                                data-test={`bad-${dataTestVal}`}
                                            />
                                        </FormField>
                                        {errorObj[apiName] && (
                                            <FormHelp
                                                error={!!errorObj[apiName]}
                                                dangerouslySetInnerHTML={{ __html: errorObj[apiName] }}
                                            />
                                        )}
                                    </Col>
                                </Row>
                            );
                        })}
                        <Row paddingY="xs">
                            <Col
                                mediaQuery={{ lg: { paddingRight: 'sm' } }}
                                md={6}
                            >
                                <FormField>
                                    <Label>Administrative Fee</Label>
                                    <Input
                                        error={errorObj.applicationFee}
                                        name="administrativeFee"
                                        placeholder="Fee"
                                        value={formData.buildingAttributes.administrativeFee ?
                                            numberUtils.asCurrency(formData.buildingAttributes.administrativeFee) : ''}
                                        onChange={e => this.props.updateFormData({
                                            buildingAttributes: {
                                                ...formData.buildingAttributes,
                                                administrativeFee: numberUtils.removeNonNumeric(e.target.value),
                                            },
                                        })}
                                        onBlur={e => savePropertyData(
                                            'administrativeFee',
                                            numberUtils.removeNonNumeric(e.target.value),
                                            { preserveFieldName }
                                        )}
                                        onFocus={() => updateOnFocus(
                                            'administrativeFee', formData.buildingAttributes.administrativeFee
                                        )}
                                        data-test="bad-administrative-fee"
                                    />
                                    {errorObj.applicationFee && (
                                        <FormHelp error={errorObj.applicationFee}>{errorObj.applicationFee}</FormHelp>)}
                                </FormField>
                            </Col>
                            <Col
                                mediaQuery={{ lg: { paddingLeft: 'sm' } }}
                                md={6}
                            >
                                <FormField>
                                    <Label>Application Fee</Label>
                                    <Input
                                        error={errorObj.applicationFee}
                                        name="applicationFee"
                                        placeholder="Fee"
                                        value={formData.buildingAttributes.applicationFee ?
                                            numberUtils.asCurrency(formData.buildingAttributes.applicationFee) : ''}
                                        onChange={e => this.props.updateFormData({
                                            buildingAttributes: {
                                                ...formData.buildingAttributes,
                                                applicationFee: numberUtils.removeNonNumeric(e.target.value),
                                            },
                                        })}
                                        onBlur={e => savePropertyData(
                                            'applicationFee',
                                            numberUtils.removeNonNumeric(e.target.value),
                                            { preserveFieldName }
                                        )}
                                        onFocus={() => updateOnFocus(
                                            'applicationFee',
                                            formData.buildingAttributes.applicationFee
                                        )}
                                        data-test="bad-application-fee"
                                    />
                                    {errorObj.applicationFee && (
                                        <FormHelp error={errorObj.applicationFee}>{errorObj.applicationFee}</FormHelp>)}
                                </FormField>
                            </Col>
                        </Row>
                        <Row paddingY="xs">
                            <Col
                                mediaQuery={{ lg: { paddingRight: 'sm' } }}
                                md={6}
                            >
                                <FormField>
                                    <Label>Minimum Deposit</Label>
                                    <Input
                                        error={errorObj.depositFeeMin}
                                        name="depositFeeMin"
                                        placeholder="Fee"
                                        value={formData.buildingAttributes.depositFeeMin ?
                                            numberUtils.asCurrency(formData.buildingAttributes.depositFeeMin) : ''}
                                        onChange={e => this.props.updateFormData({
                                            buildingAttributes: {
                                                ...formData.buildingAttributes,
                                                depositFeeMin: numberUtils.removeNonNumeric(e.target.value),
                                            },
                                        })}
                                        onBlur={e => savePropertyData(
                                            'depositFeeMin',
                                            numberUtils.removeNonNumeric(e.target.value),
                                            { preserveFieldName }
                                        )}
                                        onFocus={() => updateOnFocus(
                                            'depositFeeMin', formData.buildingAttributes.depositFeeMin
                                        )}
                                        data-test="bad-deposit-fee-min"
                                    />
                                    {errorObj.depositFeeMin && (
                                        <FormHelp error={errorObj.depositFeeMin}>{errorObj.depositFeeMin}</FormHelp>)}
                                </FormField>
                            </Col>
                            <Col
                                mediaQuery={{ lg: { paddingLeft: 'sm' } }}
                                md={6}
                            >
                                <FormField>
                                    <Label>Maximum Deposit</Label>
                                    <Input
                                        error={errorObj.depositFeeMax}
                                        name="depositFeeMax"
                                        placeholder="Fee"
                                        value={formData.buildingAttributes.depositFeeMax ?
                                            numberUtils.asCurrency(formData.buildingAttributes.depositFeeMax) : ''}
                                        onChange={e => this.props.updateFormData({
                                            buildingAttributes: {
                                                ...formData.buildingAttributes,
                                                depositFeeMax: numberUtils.removeNonNumeric(e.target.value),
                                            },
                                        })}
                                        onBlur={e => savePropertyData(
                                            'depositFeeMax',
                                            numberUtils.removeNonNumeric(e.target.value),
                                            { preserveFieldName }
                                        )}
                                        onFocus={() => updateOnFocus(
                                            'depositFeeMax', formData.buildingAttributes.depositFeeMax
                                        )}
                                        data-test="bad-deposit-fee-max"
                                    />
                                    {errorObj.depositFeeMin && (
                                        <FormHelp error={errorObj.depositFeeMin}>{errorObj.depositFeeMin}</FormHelp>)}
                                </FormField>
                            </Col>
                        </Row>
                        <Row paddingY="xs">
                            <Col
                                mediaQuery={{ lg: { paddingRight: 'sm' } }}
                                md={6}
                            >
                                <FormField>
                                    <Label>Year Built</Label>
                                    <Input
                                        error={errorObj.yearBuilt}
                                        name="yearBuilt"
                                        placeholder="Year"
                                        value={formData.buildingAttributes.yearBuilt ?
                                            formData.buildingAttributes.yearBuilt : ''}
                                        onChange={e => this.props.updateFormData({
                                            buildingAttributes: {
                                                ...formData.buildingAttributes,
                                                yearBuilt: e.target.value,
                                            },
                                        })}
                                        onBlur={e => savePropertyData('yearBuilt', e.target.value, {
                                            preserveFieldName,
                                        })}
                                        onFocus={() => updateOnFocus('yearBuilt',
                                            formData.buildingAttributes.yearBuilt)}
                                        data-test="bad-year-built"
                                    />
                                    {errorObj.yearBuilt && (
                                        <FormHelp error={errorObj.yearBuilt}>{errorObj.yearBuilt}</FormHelp>)}
                                </FormField>
                            </Col>
                            <Col
                                mediaQuery={{ lg: { paddingLeft: 'sm' } }}
                                md={6}
                            >
                                <FormField>
                                    <Label>Year Remodeled</Label>
                                    <Input
                                        error={errorObj.yearRemodeled}
                                        name="yearRemodeled"
                                        placeholder="Year"
                                        value={formData.buildingAttributes.yearRemodeled ?
                                            formData.buildingAttributes.yearRemodeled : ''}
                                        onChange={e => this.props.updateFormData({
                                            buildingAttributes: {
                                                ...formData.buildingAttributes,
                                                yearRemodeled: e.target.value,
                                            },
                                        })}
                                        onBlur={e => savePropertyData('yearRemodeled', e.target.value, {
                                            preserveFieldName,
                                        })}
                                        onFocus={() => updateOnFocus(
                                            'yearRemodeled', formData.buildingAttributes.yearRemodeled
                                        )}
                                        data-test="bad-year-remodeled"
                                    />
                                    {errorObj.yearRemodeled && (
                                        <FormHelp error={errorObj.yearRemodeled}>{errorObj.yearRemodeled}</FormHelp>)}
                                </FormField>
                            </Col>
                        </Row>
                        <Row paddingY="xs">
                            <Col
                                mediaQuery={{ lg: { paddingRight: 'sm' } }}
                                md={6}
                            >
                                <FormField>
                                    <Label>Story Count</Label>
                                    <Input
                                        error={errorObj.storyCount}
                                        name="storyCount"
                                        placeholder="Stories"
                                        value={formData.buildingAttributes.storyCount ?
                                            formData.buildingAttributes.storyCount : ''}
                                        onChange={e => this.props.updateFormData({
                                            buildingAttributes: {
                                                ...formData.buildingAttributes,
                                                storyCount: e.target.value,
                                            },
                                        })}
                                        onBlur={e => savePropertyData('storyCount', e.target.value, {
                                            preserveFieldName,
                                        })}
                                        onFocus={() => updateOnFocus('storyCount',
                                            formData.buildingAttributes.storyCount)}
                                        data-test="bad-story-count"
                                    />
                                    {errorObj.storyCount && (
                                        <FormHelp error={errorObj.storyCount}>{errorObj.storyCount}</FormHelp>)}
                                </FormField>
                            </Col>
                            <Col
                                mediaQuery={{ lg: { paddingLeft: 'sm' } }}
                                md={6}
                            >
                                <FormField>
                                    <Label>Number of Units</Label>
                                    <Input
                                        error={errorObj.numUnits}
                                        name="numUnits"
                                        placeholder="Units"
                                        value={formData.numUnits}
                                        onChange={this.updateFormData}
                                        onBlur={e => savePropertyData('numUnits', e.target.value)}
                                        onFocus={() => updateOnFocus('numUnits',
                                            formData.buildingAttributes.storyCount)}
                                        data-test="bad-number-of-units"
                                    />
                                    {errorObj.numUnits && (
                                        <FormHelp error={errorObj.numUnits}>{errorObj.numUnits}</FormHelp>)}
                                </FormField>
                            </Col>
                        </Row>
                    </div>
                )}
            </div>
        );
    }
}

export { BuildingAttributes };
export default BuildingAttributes;
